import React, { createElement, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useFormPermissions } from '../../hooks/useFormPermissions';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight, fontWeight } from '../../theme/typography';
import { toYyyyMmDd } from './formDateFieldUtils';
import type { FormDateFieldProps } from './FormDateFieldTypes';

/**
 * Web: native `<input type="date">` with the same label row and control band as `FormInput`.
 */
export const FormDateField: React.FC<FormDateFieldProps> = ({
  label,
  value,
  onDateChange,
  disabled = false,
  placeholder = 'Select date',
  required = false,
  error,
  containerStyle,
  testID,
}) => {
  const { canEditRequest } = useFormPermissions();
  const isDisabled = disabled || !canEditRequest;
  const hasError = Boolean(error);
  const iso = toYyyyMmDd(value) ?? '';

  const webInputStyle = useMemo(
    () => ({
      // Chrome/Edge: without minWidth 0, type=date can force a flex row to grow past the column.
      minWidth: 0,
      maxWidth: '100%',
      height: spacing.formControlHeight,
      minHeight: spacing.formControlHeight,
      maxHeight: spacing.formControlHeight,
      boxSizing: 'border-box' as const,
      borderWidth: 1,
      borderStyle: 'solid' as const,
      borderColor: hasError ? colors.error : colors.inputBorderNeutral,
      borderRadius: spacing.formControlBorderRadius,
      paddingLeft: spacing.formControlPaddingHorizontal,
      paddingRight: spacing.formControlPaddingHorizontal,
      width: '100%',
      fontSize: fontSize.formControl,
      lineHeight: `${lineHeight.formControl}px`,
      color: isDisabled ? colors.textMuted : colors.textPrimary,
      backgroundColor: isDisabled ? colors.backgroundSecondary : colors.backgroundForm,
      outlineStyle: 'none' as const,
      outlineWidth: 0,
      ...(!isDisabled && !hasError
        ? {
            cursor: 'pointer' as const,
          }
        : {}),
    }),
    [hasError, isDisabled],
  );

  const onInput = useCallback(
    (e: { currentTarget: { value: string } }) => {
      onDateChange(e.currentTarget.value);
    },
    [onDateChange],
  );

  return (
    <View style={[styles.formGroup, containerStyle]} testID={testID}>
      <View style={styles.labelContainer}>
        <Text style={styles.label} accessibilityRole="text">
          {label}
          {required && <Text style={styles.required}> *</Text>}
        </Text>
      </View>
      <View
        style={[
          styles.fieldRow,
          isDisabled && styles.fieldRowDisabled,
        ]}
      >
        {createElement('input', {
          type: 'date',
          value: iso,
          onChange: onInput,
          disabled: isDisabled,
          'aria-label': !iso && !isDisabled ? `${label}. ${placeholder}` : label,
          'aria-invalid': hasError,
          'data-testid': testID ? `${testID}-input` : undefined,
          style: webInputStyle,
        })}
      </View>
      {error ? (
        <Text style={styles.errorText} accessibilityRole="alert">
          {error}
        </Text>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: spacing.formFieldGroupGap,
    ...Platform.select({
      web: {
        minWidth: 0,
        maxWidth: '100%' as const,
        alignSelf: 'stretch' as const,
      },
      default: {},
    }),
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.fieldVerticalGap,
  },
  label: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  required: {
    color: colors.error,
  },
  fieldRow: {
    position: 'relative' as const,
    width: '100%' as const,
    minWidth: 0,
    maxWidth: '100%' as const,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    /**
     * Use a fixed 48px band, not `minHeight` only — some `input[type=date]` UIs grow
     * the row so the next field *looks* farther than a normal `formFieldGroupGap` step
     * (e.g. vs. Opportunity / FormInput rows).
     */
    height: spacing.formControlHeight,
    maxHeight: spacing.formControlHeight,
  },
  fieldRowDisabled: {
    opacity: 0.9,
  },
  errorText: {
    fontSize: fontSize.charCount,
    color: colors.error,
    marginTop: spacing.xs,
  },
});
